/**
 * OperationalStatusSpine — Iter 38.
 *
 * Thin tactical status strip at the bottom of the SheetViewer. NOT a
 * dashboard. NOT analytics. Five quiet pills that answer one question:
 * "Is there anything on THIS sheet I need to pay attention to?"
 *
 *   1. Sheets         — orientation (always visible)
 *   2. Active Reviews — hidden when 0
 *   3. Pending Acks   — hidden when 0
 *   4. Utility Watches — hidden when 0
 *   5. Sync Status    — hidden when 'synced'
 *
 * Drawing-First rules:
 *   - 28px tall, blurred translucent background — never solid.
 *   - Restrained orange / amber accents, never red. The strip sits BELOW
 *     critical drawing content; the canvas is taller than the viewport
 *     anyway, so the spine occupies non-drawing real estate.
 *   - Tap a pill → invokes the supplied callback. Camera + zoom NEVER
 *     mutate from within this component.
 *   - Hidden in Field Mode (FAB+swipe is the field gesture set).
 *
 * Data sources:
 *   - sheets, position counter             ← parent prop
 *   - activeReview, ackSummary             ← parent prop (already loaded
 *                                            by useAckController)
 *   - utility intel                        ← own one-shot fetch per sheet
 *   - sync state                           ← syncQueue.snapshot subscription
 */
import React, { useEffect, useState } from "react";
import { FileText, Inbox, MessageSquare, AlertTriangle, Check, Clock, Loader2, CloudOff, AlertOctagon } from "lucide-react";
import { fetchUtilityIntel } from "@/lib/utilityIntel";
import { getCombinedSnapshot, drainAll, onAnyChange } from "@/lib/syncTruth";


// ---------- Pill primitive ----------
function Pill({ icon: Icon, label, sublabel, tone = "neutral", onClick, testid, disabled, extraDataAttrs = {} }) {
  const TONES = {
    neutral: { fg: "var(--fop-text-2)", soft: "rgba(255,255,255,0.04)", border: "var(--fop-border-2)" },
    primary: { fg: "var(--fop-primary, #e85d1f)", soft: "rgba(232, 93, 31,0.10)", border: "rgba(232, 93, 31,0.45)" },
    amber:   { fg: "#f59e0b", soft: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.45)" },
    ok:      { fg: "#10b981", soft: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.45)" },
    warn:    { fg: "#ef4444", soft: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.45)" },
  };
  const t = TONES[tone] || TONES.neutral;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      className="group inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-full border text-xs font-mono uppercase tracking-wider whitespace-nowrap disabled:cursor-default disabled:opacity-100 hover:brightness-110 transition-all"
      style={{ color: t.fg, background: t.soft, borderColor: t.border, pointerEvents: onClick ? "auto" : "none" }}
      data-testid={testid}
      data-tone={tone}
      {...extraDataAttrs}
    >
      {Icon && <Icon size={14} className={extraDataAttrs["data-state"] === "syncing" ? "animate-spin" : ""} />}
      <span>{label}</span>
      {sublabel && <span className="opacity-60 ml-0.5">{sublabel}</span>}
    </button>
  );
}


// ---------- Internal: Utility intel pill (own fetch per sheet) ----------
function UtilityWatchPill({ sheetId, visible, onTap }) {
  const [intel, setIntel] = useState({ crossings: [], hotspots: [] });
  useEffect(() => {
    if (!sheetId || !visible) { setIntel({ crossings: [], hotspots: [] }); return; }
    const ac = new AbortController();
    fetchUtilityIntel(sheetId, { signal: ac.signal }).then((d) => { if (d) setIntel(d); });
    return () => ac.abort();
  }, [sheetId, visible]);

  const cN = intel.crossings.length;
  const hN = intel.hotspots.length;
  if (!cN && !hN) return null;
  const reviewCount = intel.crossings.filter((c) => c.severity === "review").length;
  const tone = reviewCount > 0 ? "primary" : "amber";
  const label = `${cN} Utility ${cN === 1 ? "Watch" : "Watches"}`;
  const sublabel = hN > 0 ? `· ${hN} Hotspot${hN === 1 ? "" : "s"}` : null;
  // Highest-severity crossing (review > watch) — tap target.
  const top = intel.crossings.find((c) => c.severity === "review") || intel.crossings[0];
  return (
    <Pill
      icon={AlertTriangle}
      label={label}
      sublabel={sublabel}
      tone={tone}
      onClick={() => onTap && onTap({ topCrossing: top, hotspots: intel.hotspots })}
      testid="spine-utility-pill"
      extraDataAttrs={{
        "data-crossings": cN,
        "data-hotspots": hN,
        "data-review-count": reviewCount,
      }}
    />
  );
}


// ---------- Internal: Sync pill — pure display, canonical sync truth ----------
// Founder "One Sync Truth" directive — all combination/drain logic lives
// in `lib/syncTruth.js`. This pill (the only sync indicator rendered on
// the Sheet Viewer page, since SheetViewer bypasses <Shell> and never
// mounts the header SyncBadge) just reflects the canonical snapshot and
// offers a manual "sync now" tap. Auto-drain-on-reconnect is driven once,
// globally, by `<SyncSuccessListener>` mounted at the App root — not
// re-implemented here, so it can never silently regress again.
const SYNC_ICON = { synced: Check, pending: Clock, syncing: Loader2, offline: CloudOff, error: AlertOctagon, attention_needed: AlertTriangle };

function SyncPill() {
  const [snap, setSnap] = useState({ state: "synced", pending: 0 });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const s = await getCombinedSnapshot();
      if (!cancelled) setSnap(s);
    };
    refresh();
    const off = onAnyChange(refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", refresh);
    const iv = setInterval(refresh, 1500);
    return () => {
      cancelled = true;
      off();
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", refresh);
      clearInterval(iv);
    };
  }, []);

  if (snap.state === "synced") return null;
  const TONE = { pending: "amber", syncing: "primary", offline: "neutral", error: "warn", attention_needed: "warn" };
  const label =
    snap.state === "offline" ? `Offline · ${snap.pending} Pending`
    : snap.state === "syncing" ? `Syncing ${snap.pending}`
    : snap.state === "error" ? `Sync Issue · ${snap.pending}`
    // FP-035 — dead-lettered work never auto-retries and needs a
    // human decision; must never render as generic "0 Pending".
    : snap.state === "attention_needed" ? `${snap.dead} Need Attention`
    : `${snap.pending} Pending`;
  return (
    <Pill
      icon={SYNC_ICON[snap.state] || Check}
      label={label}
      tone={TONE[snap.state] || "amber"}
      onClick={async () => {
        if (busy) return;
        setBusy(true);
        try { await drainAll(); } finally { setBusy(false); }
      }}
      testid="spine-sync-pill"
      extraDataAttrs={{ "data-state": busy ? "syncing" : snap.state, "data-pending": snap.pending }}
    />
  );
}


// ---------- Main component ----------
export default function OperationalStatusSpine({
  sheet,                  // {sheet_number, ...}
  sheetPosition,          // { index, total, prev, next }
  ackSummary,             // { all: {...counts}, mine: {...counts}, latest_review? }
  activeReview,           // RevisionReview | null
  utilityIntelVisible,    // bool — gates the utility pill fetch
  onTapReviews,           // () => void  — open inspector at first impact
  onTapAcks,              // () => void  — same target as reviews
  onTapUtility,           // ({topCrossing, hotspots}) => void
}) {
  // ----- Sheets pill -----
  const sheetLabel = sheet?.sheet_number || "—";
  const positionSublabel =
    sheetPosition && sheetPosition.total > 0 && sheetPosition.index >= 0
      ? `${sheetPosition.index + 1}/${sheetPosition.total}`
      : null;

  // ----- Reviews pill -----
  // V1 contract: "Active Reviews" = the count of distinct reviews carrying
  // pending acks for this sheet. Today the system always serves the most
  // recent review (Iter 32 design), so it's binary: 1 if activeReview has
  // any pending counts, else 0. Future iterations can pass a richer count.
  const activeReviewCount = activeReview ? 1 : 0;

  // ----- Acks pill — mine first, fall back to project-wide pending -----
  const mine = ackSummary?.mine || {};
  const all = ackSummary?.all || {};
  const minePending = (mine.pending || 0) + (mine.clarification || 0) + (mine.conflict || 0);
  const allPending = (all.pending || 0) + (all.clarification || 0) + (all.conflict || 0);
  const ackCount = minePending > 0 ? minePending : allPending;
  const ackScope = minePending > 0 ? "mine" : "all";

  // Iter 178.4 — UtilityWatchPill visibility is state that lives in the
  // pill's own <UtilityWatchPill>; we tap into its render side-effect via
  // an ancillary boolean tracked in the parent later if needed. For now,
  // count only Reviews + Acks toward the "should this strip render?"
  // decision — the utility pill hides itself when empty. This means
  // the strip renders when ANY of those three has real content.
  const hasAnyActionable = activeReviewCount > 0 || ackCount > 0;
  // The Sheets pill was redundant with the top-bar SHEET N OF M nav.
  // The Spine now hides completely when nothing actionable is present.
  if (!hasAnyActionable && !utilityIntelVisible) {
    // Still render an empty container ONLY so <SyncPill>'s "not synced"
    // state can appear when the operator loses connection. Everything
    // else is suppressed.
    return (
      <div
        className="absolute left-0 right-0 bottom-0 z-20 pointer-events-none"
        data-testid="operational-status-spine"
        aria-label="Operational status"
        data-spine-mode="minimal"
      >
        <div
          className="mx-auto inline-flex items-center gap-2 px-2.5 py-1 mb-1.5 ml-2.5 rounded-full pointer-events-auto empty:hidden"
        >
          <SyncPill />
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute left-0 right-0 bottom-0 z-20 pointer-events-none"
      data-testid="operational-status-spine"
      aria-label="Operational status"
      data-spine-mode="full"
    >
      <div
        className="mx-auto inline-flex items-center gap-2 px-2.5 py-1 mb-1.5 ml-2.5 rounded-full border bg-[var(--fop-surface)]/85 backdrop-blur-md pointer-events-auto shadow-lg"
        style={{ borderColor: "var(--fop-border-2)" }}
      >
        {/* Iter 178.4 — Sheets pill removed. Sheet number + N/M live in the
            top bar (identity block + sheet-position-nav). Duplicating it
            here made the bottom-left region feel like buttons stacked on
            buttons. */}

        {/* Active Reviews — hidden when 0 */}
        {activeReviewCount > 0 && (
          <Pill
            icon={Inbox}
            label={`${activeReviewCount} Active ${activeReviewCount === 1 ? "Review" : "Reviews"}`}
            tone="primary"
            onClick={onTapReviews}
            testid="spine-reviews-pill"
            extraDataAttrs={{ "data-count": activeReviewCount }}
          />
        )}

        {/* Pending Acks — hidden when 0 */}
        {ackCount > 0 && (
          <Pill
            icon={MessageSquare}
            label={`${ackCount} Pending ${ackCount === 1 ? "Ack" : "Acks"}`}
            tone="amber"
            onClick={onTapAcks}
            testid="spine-acks-pill"
            extraDataAttrs={{ "data-count": ackCount, "data-scope": ackScope }}
          />
        )}

        {/* Utility Watches — own fetch, hidden when no crossings */}
        <UtilityWatchPill
          sheetId={sheet?.id}
          visible={utilityIntelVisible}
          onTap={onTapUtility}
        />

        {/* Sync — hidden when synced */}
        <SyncPill />
      </div>
    </div>
  );
}
